import React, {
  useState,
  useEffect,
  memo,
  useCallback,
  useRef,
} from 'react';

// Redux
import { useSelector } from 'react-redux';

// Draggable Scroll

// React Virtuoso

// Material
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TableRow from '@material-ui/core/TableRow';

// Components
import { PrettiNameLine } from 'components/ReportComponents';

// Types
import { ApplicationState } from 'store';
import { EntrySimplifiedInterface } from 'store/ducks/reports/types';

// Helpers
import { returnDateAndTime } from 'helpers/date';
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';

// Contants
import BALANCE from 'constants/balance';
import CONST_QRCODE from 'constants/qrcodes';

// Styles
import useStyles from '../../chartReport.styles';

interface TableToPrintInterface {
  isHistoryDatails?: boolean;
  entrysList?: EntrySimplifiedInterface[];
  isPrinting?: boolean;
  isMounted?: 'initalState' | 'loading' | 'notLoading';
  setIsMounted?: (param: 'initalState' | 'loading' | 'notLoading') => void;
  setTableReference: any;
  setIsComponentMounted: (param: any) => void;
}

const TableToPrintComponent: React.FC<TableToPrintInterface> = ({
  isHistoryDatails,
  entrysList,
  isPrinting,
  isMounted,
  setIsMounted,
  setTableReference,
  setIsComponentMounted,
}) => {
  const classes = useStyles();

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('usedAt');
  const [grouping, setGrouping] = useState<any[]>([]);

  const chartEntryReportSimplifiedList = useSelector(
    (state: ApplicationState) =>
      entrysList?.length
        ? entrysList
        : state.reports.chartEntryReportSimplifiedList,
  );
  const entryReportListGrouped = useSelector(
    (state: ApplicationState) => state.reports.entryReportListGrouped,
  );
  useEffect(() => {
    if (entryReportListGrouped) {
      setGrouping(
        entryReportListGrouped
          .sort(
            (a: any, b: any) =>
              Math.abs(new Date(a.usedAt).getTime()) -
              Math.abs(new Date(b.usedAt).getTime()),
          )
          .map((item, index: number) => ({
            ...item,
            position: index + 1,
          })),
      );
    }
  }, [entryReportListGrouped]);

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, property: string) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy],
  );

  const formatQrcodeType = (qrcodeType: number) =>
    CONST_QRCODE.ENTRY.find((e) => e.KEY === qrcodeType)?.VALUE || '--';

  const mounted = useRef(false);
  useEffect(() => {
    if (setIsMounted) setIsMounted('loading');
    mounted.current = true;

    setIsComponentMounted(mounted);
    return () => {
      if (setIsMounted) setIsMounted('notLoading');
      mounted.current = false;
      setIsComponentMounted(mounted);
    };
  }, []);

  return (
    <TableContainer
      className={classes.table}
      id='page'
      ref={(ref) => setTableReference(ref)!}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              variant='head'
              align='left'
              className={isHistoryDatails ? classes.firstTd : ''}
              sortDirection={orderBy === 'position' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'position' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'position')}>
                ID
                {orderBy === 'position' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'line' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'line' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'line')}>
                Linha
                {orderBy === 'line' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'benefit' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'benefit' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'benefit')}>
                Benefício
                {orderBy === 'benefit' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'usedAt' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'usedAt' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'usedAt')}>
                Data
                {orderBy === 'usedAt' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'value' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'value' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'value')}>
                Valor
                {orderBy === 'value' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'type' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'type' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'type')}>
                Tipo
                {orderBy === 'type' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'qrcodeType' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'qrcodeType' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'qrcodeType')}>
                QRCode
                {orderBy === 'qrcodeType' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'id' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'id')}>
                Identificador
                {orderBy === 'id' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'latitude' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'latitude' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'latitude')}>
                Latitude
                {orderBy === 'latitude' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'longitude' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'longitude' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'longitude')}>
                Longitude
                {orderBy === 'longitude' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'Dbd.serial' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'Dbd.serial' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'Dbd.serial')}>
                DBD
                {orderBy === 'Dbd.serial' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              align='left'
              sortDirection={orderBy === 'dbdPlace' ? order : false}>
              <TableSortLabel
                classes={{
                  root: classes.sortLabel,
                  icon: classes.sortLabel,
                }}
                active
                direction={orderBy === 'dbdPlace' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'dbdPlace')}>
                Local
                {orderBy === 'dbdPlace' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stableSort(
            chartEntryReportSimplifiedList,
            getComparator(order, orderBy),
          ).map((data: any, i: number) => {
            if (i + 1 === chartEntryReportSimplifiedList.length) {
              if (setIsMounted) setIsMounted('notLoading');
            }
            return (
              <TableRow>
                <TableCell>{data.id ? data.id.substring(0, 6) : ''}</TableCell>
                <TableCell>
                  <PrettiNameLine
                    line={data.line}
                    color={data?.color || '#9427EA'}
                  />
                </TableCell>
                <TableCell>{data.benefit ? data.benefit : ''}</TableCell>
                <TableCell>
                  {data.createdAt ? returnDateAndTime(data.createdAt) : ''}
                </TableCell>
                <TableCell>
                  {`R$ ${data.value.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                  })}`}
                </TableCell>
                <TableCell>
                  {BALANCE.USAGE_TYPES.find(({ ID }) => ID === data.usageType)
                    ?.VALUE || '--'}
                </TableCell>
                <TableCell align='center'>
                  {formatQrcodeType(data?.qrcodeType)}
                </TableCell>
                <TableCell align='center'>
                  {data?.description || '--'}
                </TableCell>
                <TableCell>{data?.lat || '--'}</TableCell>
                <TableCell>{data?.lng || '--'}</TableCell>
                <TableCell>{data?.dbd || '--'}</TableCell>
                <TableCell>{data?.place || '--'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TableToPrint = memo(TableToPrintComponent);
export { TableToPrint };
export default TableToPrint;
